import { LabeledString } from "server/services/tables/utils/table-field-value-to-api-value";
import { APIField } from "server/services/tables/types/fields";
import { APIRecord } from "server/services/tables/types/records";
import type {
  KanbanRenderData,
  GroupField,
  KanbanCardState,
  KanbanState,
} from "./constants-and-types";
import { differenceBy, intersectionBy, maxBy, sortBy, keyBy } from "lodash";
import type { KanbanColumn, KanbanColumns } from "../types";

type ColumnState = KanbanState["columns"];

function reconcileKanbanColumns(
  selectedColumns: KanbanColumns,
  columnState: ColumnState | undefined = []
): [ColumnState, boolean] {
  // Remove columns that are not in the selectedColumns
  const newState = intersectionBy(columnState, selectedColumns, (e) => e.value);

  // Get the new selectedColumns
  const newSelectedColumns = differenceBy(
    selectedColumns,
    newState,
    (e) => e.value
  );

  // If no columns have been added or removed, then return the old state.
  const isChanged: boolean =
    columnState.length !== newState.length || newSelectedColumns.length > 0;
  if (!isChanged) return [columnState, false];

  // If there are no new items, we can stop here.
  if (newSelectedColumns.length === 0) return [newState, isChanged];

  // New columns will be appended, so we need the highest order.
  const maxOrder = maxBy(newState, (e) => e.order)?.order;
  const startingOrder: number = maxOrder === undefined ? 0 : maxOrder + 1;

  // Add the new items with their new ordering
  newSelectedColumns.forEach((selectedColumn, i) => {
    newState.push({
      value: selectedColumn.value,
      order: startingOrder + i,
    });
  });

  return [newState, isChanged];
}

type CardState = KanbanState["cards"];
function reconcileKanbanCards(
  records: APIRecord[],
  cardState: CardState | undefined = []
): [CardState, boolean] {
  // Remove cards that are not in the records array
  const newState = intersectionBy(cardState, records, (e) => e.id);

  // Get the new records
  const newRecords = differenceBy(records, newState, (e) => e.id);

  // If no columns have been added or removed, then return the old state.
  const isChanged: boolean =
    cardState.length !== newState.length || newRecords.length > 0;
  if (!isChanged) return [cardState, false];

  // If there are no new items, we can stop here.
  if (newRecords.length === 0) return [newState, isChanged];

  // New records will be appended, so we need the highest card order.
  const maxOrder = maxBy(newState, (e) => e.order)?.order;
  const startingOrder: number = maxOrder === undefined ? 0 : maxOrder + 1;

  newRecords.forEach((record, i) => {
    newState.push({
      id: record.id,
      order: startingOrder + i,
    });
  });

  return [newState, isChanged];
}

export function reconcileKanbanState(
  selectedColumns: KanbanColumns,
  records: APIRecord[],
  state: KanbanState | undefined = { columns: [], cards: [] }
): [KanbanState, boolean] {
  const [columns, columnsChanged] = reconcileKanbanColumns(
    selectedColumns,
    state.columns
  );
  const [cards, cardsChanged] = reconcileKanbanCards(records, state.cards);

  return [
    {
      columns,
      cards,
    },
    columnsChanged || cardsChanged,
  ];
}

export function getKanbanData({
  fields,
  records,
  groupFieldId,
  titleFieldId,
  descriptionFieldId,
  selectedColumns,
  kanbanState,
}: {
  fields: APIField[];
  records: APIRecord[];
  groupFieldId: APIField["id"];
  titleFieldId: APIField["id"] | undefined;
  descriptionFieldId: APIField["id"] | undefined;
  selectedColumns: KanbanColumns;
  kanbanState: KanbanState;
}): KanbanRenderData {
  const groupFieldKey = `f${groupFieldId}`;

  const groupField = fields.find((field) => field.id === groupFieldId) as
    | GroupField
    | undefined;
  if (!groupField)
    throw new Error("gropuFieldId references a field that does not exist");

  const fieldType = groupField.type;

  const data: {
    column: KanbanColumn;
    records: APIRecord[];
  }[] = selectedColumns.map((col) => ({ column: col, records: [] }));

  const cardOrderDictionary = keyBy(kanbanState.cards, "id");

  const sortedRecords = sortBy(
    records,
    (record) => cardOrderDictionary[record.id].order
  );

  sortedRecords.forEach((record) => {
    const fieldValue = record.data[groupFieldKey];
    if (!fieldValue) return;

    const apiValue =
      fieldType === "labeled_string"
        ? (fieldValue as LabeledString).value
        : (fieldValue as string);
    const datum = data.find((d) => d.column.value === apiValue);

    if (datum) datum.records.push(record);
  });

  const renderData: KanbanRenderData = data.map((datum) => ({
    id: datum.column.value,
    label: datum.column.label,
    cards: datum.records.map<KanbanRenderData[number]["cards"][number]>(
      (record) => {
        const descriptionField = fields.find(
          (field) => field.id === descriptionFieldId
        );
        const isButtonField =
          descriptionField?.type === "button_continue_zap" ||
          descriptionField?.type === "button_trigger_zap";
        const description =
          isButtonField && descriptionFieldId
            ? {
                ...descriptionField?.options?.button,
                fieldId: descriptionFieldId,
                recordId: record.id,
              }
            : (record.data[`f${descriptionFieldId}`] as string);
        return {
          id: record.id,
          descriptionFieldType: descriptionField?.type || "string",
          title: record.data[`f${titleFieldId}`] as string,
          description,
        };
      }
    ),
  }));

  return renderData;
}

export function reorderKanbanCard({
  state,
  movingCardId,
  targetCardId,
  placeAfter,
}: {
  state: KanbanState;
  movingCardId: KanbanCardState["id"];
  targetCardId: KanbanCardState["id"] | undefined;
  placeAfter: boolean;
}): KanbanState {
  const cards = state.cards;
  const movingCard = cards.find((card) => card.id === movingCardId);
  const targetCard = cards.find((card) => card.id === targetCardId);
  if (!movingCard) return state;

  const delta = placeAfter ? 1 : 0;
  const newOrder = targetCard ? targetCard.order + delta : 0;

  const newCards = cards.map((card) => {
    let order: number;

    if (card.id === movingCard.id) order = newOrder;
    else if (targetCard && card.order >= newOrder) order = card.order + 1;
    else return card;

    return { ...card, order };
  });

  return { ...state, cards: newCards };
}
